import { useEffect } from "react";
import { COMPANY, SITE_URL } from "./constants";
import { api } from "./api";

// CMS SEO-overrides (beheer → SEO). Eén keer per sessie opgehaald; codewaarden blijven de basis.
let overridesPromise = null;
const loadOverrides = () => {
  if (!overridesPromise) {
    overridesPromise = api.get("/seo-overrides").then((r) => r.data || []).catch(() => []);
  }
  return overridesPromise;
};

const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

// Testomgeving (preview) nooit laten indexeren; productie en prerender (127.0.0.1) wel.
const IS_PREVIEW = typeof window !== "undefined" && /\.preview\.emergentagent\.com$/.test(window.location.hostname);

export const useSEO = ({ title, description, path, ogImage, jsonLd, faqJsonLd, breadcrumbs, noindex }) => {
  useEffect(() => {
    if (title) document.title = title;
    setMeta("robots", noindex || IS_PREVIEW ? "noindex, nofollow" : "index, follow");
    if (description) {
      setMeta("description", description);
      setMeta("og:description", description, "property");
    }
    if (title) {
      setMeta("og:title", title, "property");
      setMeta("twitter:title", title);
    }
    setMeta("og:type", "website", "property");
    setMeta("og:site_name", COMPANY.name, "property");
    setMeta("twitter:card", "summary_large_image");
    if (ogImage) {
      setMeta("og:image", ogImage, "property");
      setMeta("twitter:image", ogImage);
    }
    // Canonical / og:url — gebruik altijd het productie domein (SITE_URL),
    // ook tijdens prerender op localhost, zodat Google de juiste URL ziet.
    const fullUrl = SITE_URL + (path || (typeof window !== "undefined" ? window.location.pathname : ""));
    setMeta("og:url", fullUrl, "property");
    if (noindex) {
      const c = document.querySelector('link[rel="canonical"]');
      if (c) c.remove();
    } else {
      setLink("canonical", fullUrl);
    }

    if (jsonLd) setJsonLd("ld-page", jsonLd);
    else {
      const el = document.getElementById("ld-page");
      if (el) el.remove();
    }
    if (faqJsonLd) setJsonLd("ld-faq", faqJsonLd);
    else {
      const el = document.getElementById("ld-faq");
      if (el) el.remove();
    }
    if (breadcrumbs) setJsonLd("ld-breadcrumbs", breadcrumbs);
    else {
      const el = document.getElementById("ld-breadcrumbs");
      if (el) el.remove();
    }
    const current = path || (typeof window !== "undefined" ? window.location.pathname : "");
    let cancelled = false;
    loadOverrides().then((list) => {
      const o = list.find((x) => x.path === current);
      if (!o || cancelled) return;
      if (o.title) { document.title = o.title; setMeta("og:title", o.title, "property"); }
      if (o.description) { setMeta("description", o.description); setMeta("og:description", o.description, "property"); }
      if (o.og_image) setMeta("og:image", o.og_image, "property");
      if (o.noindex) {
        setMeta("robots", "noindex, nofollow");
        const c = document.querySelector('link[rel="canonical"]');
        if (c) c.remove();
      }
    });
    return () => { cancelled = true; };
  }, [title, description, path, ogImage, noindex, JSON.stringify(jsonLd || {}), JSON.stringify(faqJsonLd || {}), JSON.stringify(breadcrumbs || {})]);
};

export const buildFaqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const buildBreadcrumbsJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.label,
    item: SITE_URL + it.href,
  })),
});

export const buildServiceJsonLd = (name, description) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  provider: { "@type": "HomeAndConstructionBusiness", "@id": `${SITE_URL}/#localbusiness`, name: COMPANY.name },
  areaServed: { "@type": "AdministrativeArea", name: "Zuid-Holland" },
});
